<template>
<div class="chapter">
<div class="inner_page">
<h1>Introduction</h1>
<div class="sub_title">
<p class="cst"> Why I Needed a Simple Guide to the Gospels</p>
</div>
<p id="imark_B02.0">It took me a long time to hear what the Gospels say. Luckily, I spent much of that
         time with the Jesuits, an organization that is patient with slow learners. Like all
         the other religious orders in the Catholic Church, the Jesuits attempt to respond
         to Jesus’ command in the Gospels to spread the Good News. So Jesuits are required
         to take time learning what’s proclaimed in the Good News. One method used in this
         learning process is the Spiritual Exercises of Ignatius Loyola. Most of those exercises
         are contemplations of Gospel scenes that are undertaken with the help of a director,
         just as physical exercises are often done with the help of a trainer. Jesuits go through
         these exercises at least twice. I did the Spiritual Exercises as a Jesuit novice;
         but when I did them again years later, I was shocked to discover I had no idea what
         I was doing.
      </p>
<p id="imark_B02.1"> The shock hit me late one afternoon as I read to my director a description of how
         the exercises had gone that day. As I read, he began to cough and clear his throat.
         He reached for a tissue and said, “Sorry; please excuse me. I’ve sometimes cried while
         listening to a write-up, but I’ve never laughed so hard.” My look must have said,
         “What’s so funny?” So he asked me to listen to what I’d been writing. After he read
         from notes he’d taken on my write-ups, he said, “Notice how you’re picturing Jesus.”
         I’d been imagining Jesus acting as a stern teacher who could barely control his impatience
         with people’s slowness to understand his message. Over and over in my prayers I had
         seen Jesus as a man who was quick to find fault with the mistakes made by his followers.
         After asking me to notice that this image wasn’t very appealing, my director reminded
         me that the Gospels describe someone quite different from the Jesus I’d imagined.
         They tell us, for instance, that many people found Jesus immensely attractive. Some
         of them even dropped everything to follow him. I had missed this simple fact. How
         was that possible?
      </p>
<p id="imark_B02.2"> At some point in my life I had slipped into the assumption that, because the Gospels
         describe a God who is infinite, it must be infinitely difficult to relate to him.
         The logic of that assumption seemed as obvious as the fact that because the theories
         of modern physics are extremely complex, physics is extremely difficult to get your
         mind around. But the Gospels aren’t complex theoretical reflections on mysterious
         truths—and they can’t be understood as such. They are four descriptions of how Jesus
         struggled to share his love of God with others, and how his struggle succeeded. The
         Gospel writers relate this success to us as simple Good News that Jesus invited others
         to enjoy and spread.
      </p>
<p id="imark_B02.3"> With the help of my retreat director, I stopped looking for hidden lessons in the
         Gospel narratives. When I began to reread the Gospels without the prejudice of my
         assumptions, it became clear that, despite many differences in the four texts, each
         evangelist’s narrative zeroed in on the same thing: Jesus’ passionate drive to teach
         by his words and his actions. Biblical scholars have pointed out that we don’t know
         precisely how the Gospel texts reached the form in which they are now presented in
         the Bible. The Gospel of Luke glances at this fact when it begins with the note that
         accounts (yes, he says “accounts”) had been handed down to the evangelist’s generation
         by those who had witnessed Jesus’ ministry [Lk.1:2]. Then the evangelist promises
         to organize this material so that the reader might come to “realize the certainty
         of the teachings” [Lk.1:4]. All the Gospel authors (or, if you like, all the editors
         and copyists who arranged the work of the original authors into the various manuscripts
         from which our modern Bibles are translated) seem to share this purpose: to make it
         plain that Jesus taught about God’s determination to bring his work of creation to
         glorious fulfillment in us, his children.
      </p>
<p id="imark_B02.4"> Years ago on retreat, when my director nudged me to take a careful look at precisely
         what the Gospels say, I began to see them as attempts to let readers hear what Jesus
         struggled to teach his first disciples to hear: good news. With my director’s encouragement
         to note the simple facts and details set down by the evangelists, I began to feel
         that even someone as benighted as myself could begin to take in the Gospel’s simple
         message.
      </p>
<h2 id="imark_B02.5">Why a Simple Guide to the Gospels Might Help You</h2>
<p id="imark_B02.6">When I look back on my difficulty in noticing Jesus’ simple proclamation of the Good
         News, I take comfort in the fact that my denseness isn’t unique. For example, when
         Mark describes Jesus visiting Nazareth, his old neighbors are said to be so astonished
         by his teaching that they couldn’t believe it. They ask, “Where did this man get all
         this?” [Mk.6:2]. What they heard seemed too good to be true, so they resolved the
         tension they felt between surprise and suspicion by choosing to be annoyed: “They
         took offense at him” [Mk.6:3]. Mark and the other evangelists relate such moments
         of rejection as dead ends—moments when the story they’re telling comes to a temporary
         halt. In other scenes, however, doubt and astonishment don’t end with a rejection
         of the Good News but lead to an awareness of its power to move the heart. For example,
         Luke describes the disciples’ first response to seeing Jesus after his resurrection
         as a mix of bafflement and glee: “They were still incredulous for joy and were amazed”
         [Lk.24:41]. Here, the disciples’ delight is said to be as real as their disbelief.
         A sense of befuddlement (“How can this be?”) grips them even as they’re filled with
         joy. One feeling doesn’t cancel the other. Luke is telling us that doubts and suspicions
         needn’t overwhelm us with dismay even when they’re striking us with full force. What
         good news!
      </p>
<p id="imark_B02.7"> If you, like me (and like many disciples before us), have been confused by parts
         of the Gospel narratives, you too might benefit from some simple comments about each
         scene and event—such as my director’s comment about the people who found Jesus fascinating.
         And you, like me, might be helped by noting that all of Jesus’ followers had to grapple
         with his simple message before they could accept it. For instance, when the Gospel
         of Mark describes events after the resurrection, it portrays Jesus taking many disciples
         to task for their stubbornness: “He appeared to them and rebuked them for their unbelief
         and hardness of heart because they had not believed those who saw him after he had
         been raised” [Mk.16:14]. Recall, however, what Jesus says next to these slow learners:
         “He said to them, ‘Go into the whole world and proclaim the Gospel to every creature’”
         [Mk.16:15]. Here Jesus entrusts the announcement of the Gospel—the Good News—to the
         very individuals who, when they first heard reports of the resurrection, couldn’t
         believe them. It’s natural to assume that, as these first disciples headed off to
         fulfill their commission to proclaim the Good News, they needed to review with one
         another what they thought the Good News was. They would have asked one another such
         questions as, “What was it he said that time we were caught in the storm; and what
         did we say in response?” As they recalled their various experiences of what Jesus
         had said and done, they would have helped one another review the recent past until
         they began to see a clear and communicable message—a message that others could grasp
         as Good News. In turn, those who heard this message began to write accounts of what
         they heard so still others could hear about Jesus and his struggle to proclaim God’s
         truth as Good News. Each Gospel proclaims this Good News, but each one proclaims it
         in a slightly different way.
      </p>
<h2 id="imark_B02.8">Some Differences in Luke’s Gospel Account</h2>
<p id="imark_B02.9">History: The Gospel according to Luke is the first volume of a two-volume work. In
         his second volume, the Acts of the Apostles, the author (whom we call Luke, though
         we can’t be certain of his identity) describes what Jesus’ followers did with the
         Good News after Jesus’ resurrection and ascension. Luke’s explicit intention was to
         write a reliable history of what Jesus taught and how his teaching spread. We know
         this because he states his purpose at the opening of his first volume by addressing
         someone called Theophilus and assuring him he’s got his facts right: “I’m writing
         a methodical report of events, Theophilus, so you can be confident in the things you’ve
         been taught.”
      </p>
<p id="imark_B02.10"> Salvation: like Mark, Luke tells stories. But, unlike Mark, he doesn’t urge the reader
         forward at a breathtaking pace. Luke carefully sets down scenes and events that reveal
         an underlying truth about the actions of the God who promised to keep his people safe
         (recall the First Commandment as it’s set down at the beginning of Chapter 20 in Exodus:
         “I, the Lord, am your God—<i>who brought you out of the land of Egypt, that place of slavery</i>. Have no other gods!” [emphasis added]). Luke wants Theophilus (and anyone else who
         reads his account) to see the full sweep of the story of how God, through Jesus, was
         faithful to the promises he made to his children—the people of Israel—and how the
         Good News invites all people to see themselves as children of God. In his Gospel,
         Luke doesn’t tell this history by listing precise places, dates, and circumstances.
         He presents a series of vignettes that reveal a simple, clear story: first, Jesus
         taught that the God who made a Covenant with Israel was trustworthy; second, Jesus’
         suffering, death, and resurrection revealed that God’s promises were indeed trustworthy;
         third, the Good News embodied in Jesus’ teaching and actions was to be shared with
         everyone.
      </p>
<p id="imark_B02.11"> Inclusivity: the New Testament epistle writer Paul may have the title Apostle to
         the Gentiles. But the Gospel of Luke tells the story of Jesus in a way that also makes
         it plain that his Good News was for anyone willing to hear it. “Here,” says Luke,
         “let me give you some examples of how that was so.”
      </p>
<p id="imark_B02.12"> For other matters stressed in Luke’s Gospel, see the index.</p>
<h2 id="imark_B02.13">A Translation Challenge</h2>
<p id="imark_B02.14">The Gospels were written in Greek. Many Gospel translations, including those in lectionaries
         used for formal church services, have been prepared by commissions of scholars. These
         translations not only render into English the words of the Greek text but also retain
         the original rhetorical phrasings. Because ancient Greek phrasing is different from
         modern English expression, a strictly literal translation is often hard to follow.
         The simplicity of an evangelist’s message can escape us when a translation retains
         its original (and unfamiliar) turns of phrase.
      </p>
<p id="imark_B02.15"> When preachers begin a homily, they often rephrase whatever text has just been proclaimed.
         They want to make sure we know what the text actually says before they begin to comment
         on it. So, after a reading, they’re likely to say something like, "Did you notice
         what the evangelist was saying there? Let me put it another way, just so we don’t
         miss his point." Like a preacher’s careful rephrasing of a text, my translation of
         Luke’s Gospel in this simple guide spells out anything that might be muddled or missed
         in a strictly literal translation of the original Greek words and phrases. The translation
         also includes occasional explanatory remarks that, in other translations, are relegated
         to footnotes or the accompanying commentary. I’ve put such explanations within the
         passages to let you keep reading Luke’s Good News without having to stop to look up
         unfamiliar references.
      </p>
<h2 id="imark_B02.16">Acknowledgments</h2>
<p id="imark_B02.17">My comments after each section of my translation are derived from the study of many
         scripture commentaries. I am indebted in particular to the richness of the notes and
         commentary by Luke Timothy Johnson, published in the Sacra Pagina series of studies
         of the New Testament, and to the wealth of information in the notes and commentary
         of Joseph A. Fitzmyer, SJ, published as part of the Anchor Bible.
      </p>
<p id="imark_B02.18"> Many people encouraged me during the writing of the manuscript and helped me with
         comments on it. Thank you to Bridget Leonard, who worked long and hard as a literary
         agent for this work, and to Carole Sargent for her guidance at Georgetown University’s
         Office of Scholarly and Literary Publication. And thank you to the parishioners at
         the parishes where I worked—especially to Dorothy Davis, Agnes Williams, Jayne Ikard,
         and Tom and Mary Biddle. Other helpful comments came from my sister, Morgan, and from
         my friends Jean Reynolds and Alan Wynroth. I am grateful to my provincial superior
         of the Maryland Province of the Jesuits, who allowed me the time to write this book,
         and to all my supportive Jesuit brothers—especially James P. M. Walsh, SJ.
      </p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 3B02introduction.vue?21cc91d8